import { AxiosResponse } from "axios";
import { ACTIONS, EXT_QUOTE_ACTIONS, FULL_QUOTE_STEPS } from "../AppConstants";
import { IPerson, IDriverBase, IDriver, ICreditCardInfo, IContactInfo, ILOBApiClient, IPaymentBag, IEftInfo } from "../classes/Domain";
import { IExternalQuote, IVehicle, IAgreement, IBindQuoteResult, IViolation, ICoverages, IQuoteQuestion } from "../classes/ExternalQuote";
import { validationUtils } from "../validation/Validation";
import { allFieldsValidation as yourHomeValidation } from "../validation/fullQuote/YourHomeValidation";
import { allFieldsValidation as carValidation } from "../validation/fullQuote/CarValidation";
import { allFieldsValidation as driverValidation } from "../validation/fullQuote/DriverValidation";
import { allFieldsValidation as additionalDriverValidation } from "../validation/fullQuote/AdditionalDriverValidation";
import { allFieldsValidation as creditCardPaymentValidation } from "../validation/fullQuote/CreditCardPaymentValidation";
import { allFieldsValidation as eftPaymentValidation } from "../validation/fullQuote/EftPaymentValidation";
import { allFieldsValidation as esigValidation } from "../validation/fullQuote/ESignatureValidation";
import { allFieldsValidation as contactInfoValidation } from "../validation/fullQuote/ContactInfoValidation";
import { allFieldsValidation as billingAddressValidation } from "../validation/fullQuote/BillingAddressValidation";
import { allViolationsValidation as violationsValidation } from "../validation/fullQuote/ViolationValidation";
import { allFieldsValidation as questionValidation } from "../validation/fullQuote/QuestionValidation";

export interface IBindQuoteResponseData {
    bindQuoteResult: IBindQuoteResult
}

export interface IBindQuoteResponse {
    data: IBindQuoteResponseData
}

export default class FullQuoteActions {
    private _apiClient: ILOBApiClient;

    constructor(apiClient: ILOBApiClient) {
        this._apiClient = apiClient;
    }

    loadQuote(quote: any)
    {
        let loadQuote = (quote: any) => (dispatch) => {

            dispatch({
                type: ACTIONS.LOAD_QUOTE,
                quote: quote
            });

            return Promise.resolve();
        };

        return loadQuote(quote);
    }

    fillExternalQuote (quote: IExternalQuote)
    {
        let fillExternalQuote = (quote: IExternalQuote) => (dispatch) => {
            dispatch({
                type: ACTIONS.FILL_EXTERNAL_QUOTE,
                quote: quote
            });

            return Promise.resolve();
        };

        return fillExternalQuote(quote);
    }

    createExternalQuote(tkn: string) {
        let createExternalQuote = (tkn: string) => (dispatch) => {

            var promise = this._apiClient.createExternalQuote(tkn)
            promise.then((response) => {
                dispatch({
                    type: ACTIONS.BUY_NOW_QUOTE,
                    quoteUid: response.data
                });
            });

            return promise;
        };

        return createExternalQuote(tkn);
    }

    fillRtrQuote (quote: IExternalQuote, paymentChoice: any)
    {
        let fillRtrQuote = (quote: IExternalQuote, paymentChoice: any) => (dispatch) => {
            dispatch({
                type: ACTIONS.FILL_RTR_QUOTE,
                quote: quote,
                paymentChoice: paymentChoice
            });

            return Promise.resolve();
        };

        return fillRtrQuote(quote, paymentChoice);
    }

    updateVehicle (
        quoteId: number,
        vehicle: IVehicle,
        paymentPlanCode: number,
        updateLienholder: boolean = false,
        updateGaragingAddress: boolean = false,
        addressSynchronization: boolean = false,
        stateCode: string | null = null)
    {
        let updateVehicle = (
            quoteId: number,
            vehicle: IVehicle,
            paymentPlanCode: number,
            updateLienholder: boolean = false,
            updateGaragingAddress: boolean = false,
            stateCode: string | null = null) => (dispatch) => {

                const { isValid, errors } = carValidation({ vehicle, stateCode });

                if (!isValid) {
                    validationUtils.publishValidationFailure(errors);
                    return Promise.reject();
                }

                var promise = this._apiClient.updateVehicle(quoteId, paymentPlanCode, vehicle, updateLienholder, updateGaragingAddress, addressSynchronization, stateCode);

                promise.then(() => {
                    dispatch({
                        type: EXT_QUOTE_ACTIONS.UPDATE_VEHICLE,
                        vehicle: vehicle
                    });
                });

                return promise;
            };

        return updateVehicle(quoteId, vehicle, paymentPlanCode, updateLienholder, updateGaragingAddress, stateCode);
    }

    updateCoverages(quoteId: number, paymentPlanCode: number, coverageConfig: ICoverages) {
        let updateCoverages = (quoteId: number, coverageConfig: ICoverages) => (dispatch) => {
            var promise = this._apiClient.updateCoverages(quoteId, paymentPlanCode, coverageConfig);

            promise.then((response) => {
                dispatch({
                    type: EXT_QUOTE_ACTIONS.UPDATE_COVERAGES,
                    coverageConfig: coverageConfig,
                    coveragesChanged: response.data.coveragesChanged
                });
            });

            return promise;
        }

        return updateCoverages(quoteId, coverageConfig);
    }

    reloadVehicle(quoteId: number, vehicleId: number, vehicleChanged: boolean, quoteRerated: boolean)
    {
        let reloadVehicle = (quoteId: number, vehicleId: number, vehicleChanged: boolean, quoteRerated: boolean) => (dispatch) => {
            var promise = this._apiClient.getVehicle(quoteId, vehicleId);

            promise.then((response) => {
                dispatch({
                    type: EXT_QUOTE_ACTIONS.UPDATE_VEHICLE,
                    vehicle: response.data,
                    vehicleChanged,
                    quoteRerated
                });
            });

            return promise;
        };

        return reloadVehicle(quoteId, vehicleId, vehicleChanged, quoteRerated);
    }

    updateAgreement (quoteId: number, agreement: IAgreement, contactInfo: IContactInfo)
    {
        let updateAgreement = (quoteId: number, agreement: IAgreement, contactInfo: IContactInfo) => (dispatch) => {
            const { isValid, errors } = contactInfoValidation(contactInfo);

            if (!isValid) {
                validationUtils.publishValidationFailure(errors);
                return Promise.reject();
            }

            var promise = this._apiClient.updateContactInfo(quoteId, contactInfo);

            promise.then((response) => {

                dispatch({
                    type: EXT_QUOTE_ACTIONS.UPDATE_AGREEMENT,
                    agreement: agreement,
                    contactInfo: contactInfo,
                    esignatureForms: response.data.esignatureForms
                });
            });

            return promise;
        };

        return updateAgreement(quoteId, agreement, contactInfo);
    }


    resetAgreement ()
    {
        let resetAgreement = () => (dispatch) => {
            dispatch({
                type: EXT_QUOTE_ACTIONS.RESET_AGREEMENT
            });
        };

        return resetAgreement;
    }


    savePaymentInfo(quoteId: number, methodPaymentCd: string, paymentInfoBag: IPaymentBag)
    {
        let savePaymentInfo = (quoteId: number, methodPaymentCd: string, paymentInfoBag: IPaymentBag) => (dispatch) => {
            // dispatch == store.dispatch

            const { isValid, errors } = creditCardPaymentValidation({ paymentInstrument: paymentInfoBag.downPaymentInstrument as ICreditCardInfo, isInstallment: false });
            if (!isValid) {
                validationUtils.publishValidationFailure(errors);
                return Promise.reject();
            }

            if (paymentInfoBag.installmentInstrument && paymentInfoBag.installmentInstrument.instrumentType === "CC") {
                const { isValid, errors } = creditCardPaymentValidation({ paymentInstrument: paymentInfoBag.installmentInstrument as ICreditCardInfo, isInstallment: true });
                if (!isValid) {
                    validationUtils.publishValidationFailure(errors);
                    return Promise.reject();
                }
            }

            if (paymentInfoBag.installmentInstrument && paymentInfoBag.installmentInstrument.instrumentType === "EFT") {
                const { isValid, errors } = eftPaymentValidation({ installmentInstrument: paymentInfoBag.installmentInstrument as IEftInfo });
                if (!isValid) {
                    validationUtils.publishValidationFailure(errors);
                    return Promise.reject();
                }
            }

            {
                const { isValid, errors } = billingAddressValidation({ billingAddress: paymentInfoBag.downPaymentInstrument.billingAddress });
                if (!isValid) {
                    validationUtils.publishValidationFailure(errors);
                    return Promise.reject();
                }
            }

            var promise = this._apiClient.savePaymentInfo(quoteId, methodPaymentCd, paymentInfoBag);

            promise.then((response) => {
                dispatch({
                    type: EXT_QUOTE_ACTIONS.UPDATE_PAYMENT_PLAN,
                    paymentPlanCode: response.data.paymentPlanCode
                });
            });

            return promise;
        };

        return savePaymentInfo(quoteId, methodPaymentCd, paymentInfoBag);
    }

    updateApplicantMailingAddress(quoteId: number, applicant: IPerson, contactInfo: IContactInfo)
    {
        let updateApplicantMailingAddress = (quoteId: number, applicant: IPerson, contactInfo: IContactInfo) => (dispatch) => {
            {
                const { isValid, errors } = yourHomeValidation(applicant);
                if (!isValid) {
                    validationUtils.publishValidationFailure(errors);
                    return Promise.reject();
                }
            }

            {
                const { isValid, errors } = contactInfoValidation(contactInfo);
                if (!isValid) {
                    validationUtils.publishValidationFailure(errors);
                    return Promise.reject();
                }
            }

            var promise = this._apiClient.updateApplicantMailingAddress(quoteId, applicant, contactInfo);

            promise.then((response) => {
                dispatch({
                    type: EXT_QUOTE_ACTIONS.UPDATE_APPLICANT_ADDRESS,
                    address: applicant.address,
                    contactInfo: contactInfo
                });
            });

            return promise;
        }

        return updateApplicantMailingAddress(quoteId, applicant, contactInfo);
    };

    updateDriver (quoteId: number, paymentPlanCode: number, driver: IDriver)
    {
        let updateDriver = (quoteId: number, paymentPlanCode: number, driver: IDriver) => (dispatch) => {
            const { isValid, errors } = driverValidation(driver);
            if (!isValid) {
                validationUtils.publishValidationFailure(errors);
                return Promise.reject();
            }

            var promise = this._apiClient.updateDriver(quoteId, paymentPlanCode, driver);

            promise.then((result) => {
                dispatch({
                    type: EXT_QUOTE_ACTIONS.UPDATE_DRIVER,
                    driver: driver,
                    driverChanged: result.data.driverChanged,
                    quoteRerated: result.data.isRerated
                });
            });

            return promise;
        };

        return updateDriver(quoteId, paymentPlanCode, driver);
    }

    getViolations (quoteId: number)
    {
        let getViolations = (quoteId: number) => (dispatch) => {
            var promise = this._apiClient.getViolations(quoteId);

            promise.then((response) => {
                dispatch({
                    type: EXT_QUOTE_ACTIONS.UPDATE_VIOLATIONS,
                    mvrResult: response.data
                });
            });

            return promise;
        };

        return getViolations(quoteId);
    }


    getDocuments (rtrQuoteId: number)
    {
        let getDocuments = (rtrQuoteId: number) => (dispatch) => {
            var promise = this._apiClient.getDocuments(rtrQuoteId);

            promise.then((response) => {

                dispatch({
                    type: EXT_QUOTE_ACTIONS.UPDATE_DOCUMENTS,
                    documents: response.data
                });
            });

            return promise;
        };

        return getDocuments(rtrQuoteId);
    }


    bindQuote (quoteId: number)
    {
        let bindQuote = (quoteId: number) => (dispatch): Promise<IBindQuoteResponse> => {
            var promise = this._apiClient.bindQuote(quoteId);

            promise.then((response) => {

                dispatch({
                    type: EXT_QUOTE_ACTIONS.BIND_QUOTE,
                    bindResult: response.data.bindQuoteResult
                });
            });

            return promise;
        };

        return bindQuote(quoteId);
    }

    reRateQuote (quoteId: number, paymentPlanCode: number, hasMvrViolations: boolean = false, hasAdditionalDrivers: boolean = false)
    {
        let reRateQuote = (quoteId: number, paymentPlanCode: number, hasMvrViolations: boolean, hasAdditionalDrivers: boolean) => (dispatch) => {
            var promise = this._apiClient.reRateQuote(quoteId, paymentPlanCode);

            promise.then((response) => {
                dispatch({
                    type: EXT_QUOTE_ACTIONS.RERATE_QUOTE,
                    data: { ...response.data, hasMvrViolations, hasAdditionalDrivers }
                });
            });

            return promise;
        };

        return reRateQuote(quoteId, paymentPlanCode, hasMvrViolations, hasAdditionalDrivers);
    }


    reSetQuoteEffDate(quoteId: number, paymentPlanCode: number, effectiveDate: string, effectiveDatetimeStamp: string)
    {
        let reSetQuoteEffDate = (quoteId: number, paymentPlanCode: number, effectiveDate: string) => (dispatch) => {
            var promise = this._apiClient.reSetQuoteEffDate(quoteId, paymentPlanCode, effectiveDate);

            promise.then((response) => {
                dispatch({
                    type: EXT_QUOTE_ACTIONS.RESET_QUOTE_EFF_DATE,
                    data: { ...response.data, effectiveDate },
                    effectiveDatetimeStamp: effectiveDatetimeStamp
                });
            });

            return promise;
        };

        return reSetQuoteEffDate(quoteId, paymentPlanCode, effectiveDate);
    }

    reSetQuoteTimeStampEffDate(effectiveDatetimeStamp: string)
    {
        let reSetQuoteTimeStampEffDate = (effectiveDatetimeStamp: string) => (dispatch) => {
            dispatch({
                type: EXT_QUOTE_ACTIONS.RESET_QUOTE_TIME_STAMP_EFF_DATE,
                effectiveDatetimeStamp: effectiveDatetimeStamp
            });
        };

        return reSetQuoteTimeStampEffDate(effectiveDatetimeStamp);
    }


    reloadQuotePaymentInfo (quoteId: number, quoteRerated: boolean = true)
    {
        let reloadQuotePaymentInfo = (quoteId: number, quoteRerated: boolean) => (dispatch) => {
            var promise = this._apiClient.getPaymentInfo(quoteId);

            promise.then((response) => {
                dispatch({
                    type: EXT_QUOTE_ACTIONS.RELOAD_QUOTE_PMT_INFO,
                    data: { ...response.data, quoteRerated }
                });
            });

            return promise;
        };

        return reloadQuotePaymentInfo(quoteId, quoteRerated);
    }


    disputeViolations (quoteId: number, driverId: number, violationsForDispute: Array<IViolation>)
    {
        let disputeViolations = (quoteId: number, driverId: number, violationsForDispute: Array<IViolation>) => () => {
            const { isValid, errors } = violationsValidation(violationsForDispute);

            if (!isValid) {
                validationUtils.publishValidationFailure(errors);
                return Promise.reject();
            }

            return this._apiClient.disputeViolations(quoteId, driverId, violationsForDispute);
        };

        return disputeViolations(quoteId, driverId, violationsForDispute);
    }


    getESignatureForms (rtrQuoteId: number)
    {
        let getESignatureForms = (rtrQuoteId: number) => (dispatch) => {

            var promise = this._apiClient.getESignatureForms(rtrQuoteId);

            promise.then((response) => {
                console.log(response.data);
                dispatch({
                    type: EXT_QUOTE_ACTIONS.FILL_E_SIGNATURES,
                    esignatureForms: response.data.esignatureForms
                });
            });

            return promise;
        };

        return getESignatureForms(rtrQuoteId);
    }

    updateQuestions(quoteId: number, questions: Array<IQuoteQuestion>) {
        let updateQuestions = (quoteId: number, questions: Array<IQuoteQuestion>) => (dispatch) => {
            let check = false;

            questions.forEach((question) => {
                const { isValid, errors } = questionValidation(question);
                if (!isValid) {
                    validationUtils.publishValidationFailure(errors);
                    check = true;
                }
            });

            if (check) {
                return Promise.reject();
            }

            var promise = this._apiClient.updateQuoteQuestions(quoteId, questions);

            promise.then(() => {
                dispatch({
                    type: EXT_QUOTE_ACTIONS.UPDATE_QUOTE_QUESTIONS,
                    questions
                });
            });

            return promise;
        };

        return updateQuestions(quoteId, questions);
    }

    updateESignatureAgreement (rtrQuoteId: number, esignatureCode: string, insuredName: string, needToSign: boolean, lastForm: boolean, options: any[])
    {
        let updateESignatureAgreement = (rtrQuoteId: number, esignatureCode: string, insuredName: string, needToSign: boolean, lastForm: boolean, options: any[]) => (dispatch) => {
            if (needToSign) {
                const data = { insuredName, options };
                const { isValid, errors } = esigValidation(data);

                if (!isValid) {
                    validationUtils.publishValidationFailure(errors);
                    return Promise.reject();
                }
            }

            const promise = this._apiClient.updateESignatureAgreement(rtrQuoteId, esignatureCode, insuredName, needToSign, lastForm, options);
            return promise;
        };

        return updateESignatureAgreement(rtrQuoteId, esignatureCode, insuredName, needToSign, lastForm, options);
    }


    updateAdditionalDrivers(quoteId: number, paymentPlanCode: number, additionalDrivers: Array<IDriver>)
    {
        let updateAdditionalDrivers = (quoteId: number, additionalDrivers: Array<IDriver>) => (dispatch) => {
            var hasValidationErrors = false;

            additionalDrivers.filter(x => !x.isExcluded).map((data, i) => {
                let { isValid, errors } = additionalDriverValidation(data);
                if (!isValid) {
                    var uid = FULL_QUOTE_STEPS.ADDITIONAL_DRIVERS + "_" + data.id;
                    validationUtils.publishValidationFailureByUid(errors, uid);
                    hasValidationErrors = true;
                }
            });

            if (hasValidationErrors) {
                return Promise.reject();
            }

            var promise = this._apiClient.updateAdditionalDrivers(quoteId, paymentPlanCode, additionalDrivers);
            return promise;
        };

        return updateAdditionalDrivers(quoteId, additionalDrivers);
    }

    resetChangedFlags = () => (dispatch) => {
        dispatch({
            type: EXT_QUOTE_ACTIONS.RESET_CHANGED_FLAGS
        });
    };
}